import { useEffect, useState } from "react";
import { Trash2, ArrowUp, ArrowDown, Plus, Image as ImageIcon, Video, Link as LinkIcon, Users, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, ComunidadNewsFeed, ComunidadNewsItem } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import {
  clearLocalDraft,
  hasContent,
  readLocalDraft,
  saveLocalDraft,
  useLocalDraft,
  useWarnBeforeLeaving,
} from "../useLocalDraft";
import { RichTextEditor } from "./RichTextEditor";
import { htmlToPlainText, isRichHtml } from "../richText";
import {
  DEFAULT_AUDIENCE,
  NewsAudience,
  NewsAudiencePicker,
  describeAudience,
  isAudienceValid,
} from "./NewsAudiencePicker";

interface NewsFeedEditorProps {
  feed: ComunidadNewsFeed;
  title: string;
}

const EMPTY_FORM: {
  title: string;
  body: string;
  imageUrl: string;
  videoUrl: string;
  linkLabel: string;
  linkUrl: string;
  audience: NewsAudience;
} = { title: "", body: "", imageUrl: "", videoUrl: "", linkLabel: "", linkUrl: "", audience: DEFAULT_AUDIENCE };

/** Editor de una columna de novedades (borrador) — reusado tanto para "Novedades Psi
 *  Platform" como para "Institucional", solo cambia el `feed` y el título de la sección.
 *  Cada novedad tiene un título, un texto (puede ser el paso a paso completo, sin resumir)
 *  y, opcional, una imagen, un video y un botón de link — estos tres últimos solo se ven en
 *  el archivo completo ("Ver todas"), nunca en el adelanto chico de la sidebar. */
export const NewsFeedEditor = ({ feed, title }: NewsFeedEditorProps) => {
  const [items, setItems] = useState<ComunidadNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Respaldo en este navegador de lo que se está escribiendo (ver useLocalDraft).
  const [newForm, setNewForm, clearNewForm] = useLocalDraft(`news-${feed}-new`, EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState(EMPTY_FORM);
  const editDraftKey = editingId ? `news-edit-${editingId}` : null;
  // Lo que tenía la edición al abrirla: si no se cambió nada, no hay nada que respaldar.
  const [editingOriginal, setEditingOriginal] = useState("");
  useEffect(() => {
    if (!editDraftKey) return;
    if (JSON.stringify(editingForm) === editingOriginal) clearLocalDraft(editDraftKey);
    else saveLocalDraft(editDraftKey, editingForm);
  }, [editDraftKey, editingForm, editingOriginal]);
  useWarnBeforeLeaving(hasContent(newForm) || (!!editingId && JSON.stringify(editingForm) !== editingOriginal));
  // Las novedades ya cargadas arrancan plegadas (solo el título) para que la lista no tape
  // el formulario de alta; se despliegan de a una para ver todo o editar.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const reload = async () => {
    setLoading(true);
    try {
      const data = await comunidadAdapter.listDraftNews(feed);
      setItems(data);
    } catch {
      toastUtils.error(`No pudimos cargar «${title}». Probá de nuevo en un rato.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed]);

  const handleAdd = async () => {
    const titleValue = newForm.title.trim();
    const bodyValue = newForm.body.trim();
    if (!titleValue || !bodyValue) return;
    if (!isAudienceValid(newForm.audience)) {
      toastUtils.error("Tildá al menos una categoría en «¿Quién puede ver esto?»");
      return;
    }
    try {
      await comunidadAdapter.createNewsItem(
        feed,
        titleValue,
        bodyValue,
        newForm.imageUrl,
        newForm.videoUrl,
        newForm.linkLabel,
        newForm.linkUrl,
        newForm.audience,
      );
    } catch {
      toastUtils.error("No se pudo agregar la novedad. Probá de nuevo.");
      return;
    }
    clearNewForm();
    toastUtils.success("Novedad agregada al borrador. Para que la vean, tocá «Publicar cambios».");
    reload();
  };

  const startEdit = (item: ComunidadNewsItem) => {
    const fromServer = {
      title: item.title,
      body: item.body,
      imageUrl: item.imageUrl ?? "",
      videoUrl: item.videoUrl ?? "",
      linkLabel: item.linkLabel ?? "",
      linkUrl: item.linkUrl ?? "",
      audience: { audienceMode: item.audienceMode ?? "all", audienceCategories: item.audienceCategories ?? [] },
    };
    setEditingOriginal(JSON.stringify(fromServer));
    setEditingId(item.id);
    // Si quedaron cambios sin guardar de esta novedad (recarga, publicar a mitad), se recuperan.
    const pending = readLocalDraft<typeof EMPTY_FORM>(`news-edit-${item.id}`);
    if (pending && hasContent(pending)) {
      setEditingForm({ ...EMPTY_FORM, ...pending });
      toastUtils.info("Recuperamos los cambios que no habías guardado de esta novedad.");
      return;
    }
    setEditingForm(fromServer);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const titleValue = editingForm.title.trim();
    const bodyValue = editingForm.body.trim();
    if (!titleValue || !bodyValue) return;
    if (!isAudienceValid(editingForm.audience)) {
      toastUtils.error("Tildá al menos una categoría en «¿Quién puede ver esto?»");
      return;
    }
    try {
      await comunidadAdapter.updateNewsItem(
        editingId,
        titleValue,
        bodyValue,
        editingForm.imageUrl,
        editingForm.videoUrl,
        editingForm.linkLabel,
        editingForm.linkUrl,
        editingForm.audience,
      );
    } catch {
      toastUtils.error("No se pudo guardar la novedad. Probá de nuevo.");
      return;
    }
    clearLocalDraft(`news-edit-${editingId}`);
    setEditingId(null);
    reload();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Eliminar esta novedad del borrador?")) return;
    await comunidadAdapter.deleteNewsItem(id);
    reload();
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setItems(reordered);
    await comunidadAdapter.reorderNews(feed, reordered.map((i) => i.id));
  };

  return (
    <div className="flex flex-col gap-3 rounded-[20px] bg-white p-6 shadow-[0_6px_20px_rgba(109,87,188,0.08)]">
      <h3 className="m-0 text-base font-bold text-[#2D2049]">{title}</h3>

      {/* Alta arriba de todo: para cargar una novedad no hace falta bajar hasta el final. */}
      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-[#C9BCE8] bg-[#FAF8FF] p-3">
        <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Nueva novedad</span>
        <input
          className="rounded-lg border border-[#E3D9FF] px-3 py-2 text-sm"
          placeholder="Título de la nueva novedad…"
          value={newForm.title}
          onChange={(e) => setNewForm((f) => ({ ...f, title: e.target.value }))}
        />
        <RichTextEditor
          idPrefix={`news-${feed}-new`}
          placeholder="Texto completo (sin resumir) — puede incluir el paso a paso…"
          value={newForm.body}
          onChange={(body) => setNewForm((f) => ({ ...f, body }))}
        />
        <input
          className="rounded-lg border border-[#E3D9FF] px-3 py-2 text-xs"
          placeholder="URL de imagen (opcional)"
          value={newForm.imageUrl}
          onChange={(e) => setNewForm((f) => ({ ...f, imageUrl: e.target.value }))}
        />
        <input
          className="rounded-lg border border-[#E3D9FF] px-3 py-2 text-xs"
          placeholder="URL de video — YouTube/Loom (opcional)"
          value={newForm.videoUrl}
          onChange={(e) => setNewForm((f) => ({ ...f, videoUrl: e.target.value }))}
        />
        <div className="flex gap-2">
          <input
            className="w-1/2 rounded-lg border border-[#E3D9FF] px-3 py-2 text-xs"
            placeholder="Texto del botón (ej. Consultá la política completa)"
            value={newForm.linkLabel}
            onChange={(e) => setNewForm((f) => ({ ...f, linkLabel: e.target.value }))}
          />
          <input
            className="w-1/2 rounded-lg border border-[#E3D9FF] px-3 py-2 text-xs"
            placeholder="URL del botón (opcional)"
            value={newForm.linkUrl}
            onChange={(e) => setNewForm((f) => ({ ...f, linkUrl: e.target.value }))}
          />
        </div>
        <NewsAudiencePicker value={newForm.audience} onChange={(audience) => setNewForm((f) => ({ ...f, audience }))} />
        <Button size="sm" className="self-start" onClick={handleAdd}>
          <Plus size={15} className="mr-1" />
          Agregar
        </Button>
      </div>

      {items.length > 0 && (
        <span className="pt-1 text-xs font-bold uppercase tracking-wide text-[#8475B3]">Ya cargadas</span>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress /></div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.length === 0 && (
            <p className="m-0 text-sm text-gray-400">Sin novedades en el borrador todavía.</p>
          )}
          {items.map((item, index) => {
            const isEditing = editingId === item.id;
            const isExpanded = isEditing || expandedIds.has(item.id);
            const audienceLabel = describeAudience({
              audienceMode: item.audienceMode ?? "all",
              audienceCategories: item.audienceCategories ?? [],
            });
            return (
            <div key={item.id} className="flex flex-col gap-2 rounded-xl border border-[#E3D9FF] px-3 py-2.5">
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => toggleExpanded(item.id)}
                  className="flex w-full items-center gap-2 text-left"
                >
                  <span className="flex-grow text-sm font-semibold text-[#2D2049]">{item.title}</span>
                  {audienceLabel && (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#EDE6FA] px-2 py-0.5 text-[11px] font-semibold text-[#665090]">
                      <Users size={12} />
                      {audienceLabel}
                    </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`shrink-0 text-[#665090] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                  />
                </button>
              )}
              {!isExpanded ? null : isEditing ? (
                <div className="flex flex-col gap-2">
                  <input
                    autoFocus
                    className="rounded-lg border border-[#C9BCE8] px-2 py-1 text-sm font-semibold"
                    value={editingForm.title}
                    onChange={(e) => setEditingForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Título"
                  />
                  <RichTextEditor
                    idPrefix={`news-edit-${item.id}`}
                    value={editingForm.body}
                    onChange={(body) => setEditingForm((f) => ({ ...f, body }))}
                    placeholder="Texto completo (sin resumir) — puede incluir el paso a paso"
                  />
                  <input
                    className="rounded-lg border border-[#C9BCE8] px-2 py-1 text-xs"
                    value={editingForm.imageUrl}
                    onChange={(e) => setEditingForm((f) => ({ ...f, imageUrl: e.target.value }))}
                    placeholder="URL de imagen (opcional)"
                  />
                  <input
                    className="rounded-lg border border-[#C9BCE8] px-2 py-1 text-xs"
                    value={editingForm.videoUrl}
                    onChange={(e) => setEditingForm((f) => ({ ...f, videoUrl: e.target.value }))}
                    placeholder="URL de video — YouTube/Loom (opcional)"
                  />
                  <div className="flex gap-2">
                    <input
                      className="w-1/2 rounded-lg border border-[#C9BCE8] px-2 py-1 text-xs"
                      value={editingForm.linkLabel}
                      onChange={(e) => setEditingForm((f) => ({ ...f, linkLabel: e.target.value }))}
                      placeholder="Texto del botón (ej. Consultá la política completa)"
                    />
                    <input
                      className="w-1/2 rounded-lg border border-[#C9BCE8] px-2 py-1 text-xs"
                      value={editingForm.linkUrl}
                      onChange={(e) => setEditingForm((f) => ({ ...f, linkUrl: e.target.value }))}
                      placeholder="URL del botón (opcional)"
                    />
                  </div>
                  <NewsAudiencePicker
                    value={editingForm.audience}
                    onChange={(audience) => setEditingForm((f) => ({ ...f, audience }))}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <span className="line-clamp-3 whitespace-pre-line text-xs text-gray-500">
                    {isRichHtml(item.body) ? htmlToPlainText(item.body) : item.body}
                  </span>
                  {(item.imageUrl || item.videoUrl || item.linkUrl) && (
                    <div className="mt-1 flex items-center gap-3">
                      {item.imageUrl && (
                        <span className="flex items-center gap-1 text-[11px] text-[#8475B3]">
                          <ImageIcon size={13} /> con imagen
                        </span>
                      )}
                      {item.videoUrl && (
                        <span className="flex items-center gap-1 text-[11px] text-[#8475B3]">
                          <Video size={13} /> con video
                        </span>
                      )}
                      {item.linkUrl && (
                        <span className="flex items-center gap-1 text-[11px] text-[#8475B3]">
                          <LinkIcon size={13} /> con link: {item.linkLabel || item.linkUrl}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isExpanded && (
              <div className="flex items-center gap-2 self-end">
                <button type="button" className="text-[#8475B3] disabled:opacity-30" disabled={index === 0} onClick={() => move(index, -1)}>
                  <ArrowUp size={15} />
                </button>
                <button type="button" className="text-[#8475B3] disabled:opacity-30" disabled={index === items.length - 1} onClick={() => move(index, 1)}>
                  <ArrowDown size={15} />
                </button>

                {editingId === item.id ? (
                  <Button size="sm" onClick={saveEdit}>Guardar</Button>
                ) : (
                  <Button size="sm" variant="outline" className="border-[#C9BCE8] text-[#665090]" onClick={() => startEdit(item)}>
                    Editar
                  </Button>
                )}
                <button type="button" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

    </div>
  );
};
